import { logger } from '@/utils/logger';
import type { Card } from '@/utils/models/cards';
import { getAppInstance } from '@/utils/api/app';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@zopauk/nextjs-auth';
import type { AxiosResponse, AxiosError } from 'axios';

interface CardError {
  message: string;
  data: unknown;
  url: string;
}

interface CardsResponse {
  discovery: Cards;
  product: Cards;
}

interface Cards {
  errors: CardError[];
  cards: Card[];
}

export const useQueryCards = () => {
  const { data: session } = useSession();

  const cardsQuery = useQuery<AxiosResponse<CardsResponse>, AxiosError>({
    queryFn: () => getAppInstance().get<CardsResponse>('/cards'),
    queryKey: ['cards', session?.user.memberId],
    enabled: !!session,
  });

  if (cardsQuery.isFetched && cardsQuery.data?.data?.product?.errors?.length) {
    logger.error({
      correlationId: cardsQuery.data?.config?.headers?.['X-Correlation-Id'],
      message: 'Errors have occurred in the product cards',
      errors: cardsQuery.data?.data.product.errors,
      memberId: session?.user.memberId,
      id: 'product-card-errors',
    });
  }

  if (cardsQuery.isFetched && cardsQuery.data?.data?.discovery?.errors?.length) {
    logger.error({
      correlationId: cardsQuery.data?.config?.headers?.['X-Correlation-Id'],
      message: 'Errors have occurred in the discovery cards',
      errors: cardsQuery.data?.data.discovery.errors,
      memberId: session?.user.memberId,
      id: 'discovery-card-errors',
    });
  }

  return cardsQuery;
};
